import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { config } from '../constants/config';

/**
 * OPPORTUNITY FILTERS SLICE
 * 
 * ARCHITECTURE NOTE:
 * This slice manages opportunity FILTERS and UI state
 * Works in tandem with opportunityDataSlice which manages DATA
 * 
 * Responsibilities:
 * - Filter state (basic: searchTerm, status, genres)
 * - Advanced filters (authors, books, characters, priority)
 * - Filter UI state (expanded/collapsed, search mode)
 * - Saved filter favorites
 * - Filter dropdown options
 * 
 * Does NOT manage:
 * - Opportunity data storage
 * - CRUD operations
 * - Individual opportunity state
 * 
 * Integration:
 * This slice provides filter configuration that opportunityDataSlice
 * uses to fetch opportunities via fetchOpportunities(filters)
 */

// Async thunk for fetching filter options
export const fetchFilterOptions = createAsyncThunk(
  'opportunityMarketplace/fetchFilterOptions',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      // Use the new zone endpoint for all filter options
      const response = await fetch(`${config.BASE_URL}/opportunities/zone/filters/options`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch filter options: ${response.status}`);
      }

      const data = await response.json();

      // The endpoint returns authors, genres, statuses, priorities, and litGroups
      return {
        authors: data.authors || [],
        litGroups: data.litGroups || [],
        genres: data.genres || [],
        statuses: data.statuses || ['Open', 'Assigned', 'Completed', 'Cancelled'],
        priorities: data.priorities || ['High', 'Medium', 'Low']
      };
    } catch (error) {
      console.error('Error fetching filter options:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching books by author(s)
export const fetchBooksByAuthor = createAsyncThunk(
  'opportunityMarketplace/fetchBooksByAuthor',
  async (authorIds, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      // Support both single author (string) and multiple authors (array)
      let url;
      if (Array.isArray(authorIds) && authorIds.length > 0) {
        // Multiple authors - use query parameter
        const idsParam = authorIds.join(',');
        url = `${config.BASE_URL}/opportunities/zone/filters/books/_?authorIds=${idsParam}`;
      } else if (typeof authorIds === 'string') {
        // Single author - backward compatibility
        url = `${config.BASE_URL}/opportunities/zone/filters/books/${authorIds}`;
      } else {
        // Default to first author ID if array with single item
        url = `${config.BASE_URL}/opportunities/zone/filters/books/${authorIds}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch books: ${response.status}`);
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error('Error fetching books by author(s):', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching characters by book
export const fetchCharactersByBook = createAsyncThunk(
  'opportunityMarketplace/fetchCharactersByBook',
  async (bookId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      // Use the new zone endpoint for characters by book
      const response = await fetch(`${config.BASE_URL}/opportunities/zone/filters/characters/${bookId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch characters: ${response.status}`);
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error('Error fetching characters by book:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * ⭐ THE LITGROUP CASCADE. Selecting a Literary Group fills the Book dropdown AND the Character
 * list; choosing a Book then REFINES the characters rather than being the only way to populate
 * them. Before this the cascade ran AUTHOR → books → characters, so picking a Literary Group
 * narrowed the results and left both dependent dropdowns empty — and the Character dropdown was
 * `disabled` until a Book was chosen, so the only way to enable it was also the only way to reach
 * an endpoint that returned nothing (see getCharactersByBook, which traversed a relationship that
 * does not exist).
 *
 * ⚠️ Book selection stays OPTIONAL — a Voice Actor may want every character in the series.
 */
export const fetchBooksByLitGroup = createAsyncThunk(
  'opportunityMarketplace/fetchBooksByLitGroup',
  async (litGroupUuid, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return rejectWithValue('No authentication token found');
      const response = await fetch(
        `${config.BASE_URL}/opportunities/zone/filters/litgroup/${litGroupUuid}/books`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error(`Failed to fetch books: ${response.status}`);
      return (await response.json()) || [];
    } catch (error) {
      console.error('Error fetching books by literary group:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCharactersByLitGroup = createAsyncThunk(
  'opportunityMarketplace/fetchCharactersByLitGroup',
  async (litGroupUuid, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return rejectWithValue('No authentication token found');
      const response = await fetch(
        `${config.BASE_URL}/opportunities/zone/filters/litgroup/${litGroupUuid}/characters`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error(`Failed to fetch characters: ${response.status}`);
      return (await response.json()) || [];
    } catch (error) {
      console.error('Error fetching characters by literary group:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for searching opportunities
export const searchOpportunities = createAsyncThunk(
  'opportunityMarketplace/searchOpportunities',
  async (filters, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      // Use the new zone search endpoint
      const response = await fetch(`${config.BASE_URL}/opportunities/zone/search`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(filters)
      });

      if (!response.ok) {
        throw new Error(`Failed to search opportunities: ${response.status}`);
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error('Error searching opportunities:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Favorites management
const loadFavoritesFromStorage = () => {
  try {
    const stored = localStorage.getItem('opportunity-filter-favorites');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveFavoritesToStorage = (favorites) => {
  try {
    localStorage.setItem('opportunity-filter-favorites', JSON.stringify(favorites));
  } catch (error) {
    console.error('Failed to save favorites:', error);
  }
};

// Initial state
const initialState = {
  // Zone-based structure
  zone: 'opportunityMarketplace',

  // Filters
  filters: {
    basic: {
      searchTerm: '',
      status: 'Open', // Default to Open
      genres: [] // Array for multi-select
    },
    advanced: {
      litGroupId: null, // Filter by Literary Group
      authorIds: [], // Changed to array for multi-select
      bookId: null,
      characterId: null,
      priority: null
    },
    // Prepared for future expansion
    future: {
      language: null,
      country: null,
      rating: null
    }
  },

  // Filter options
  filterOptions: {
    authors: [],
    litGroups: [], // LitGroups with opportunities
    books: [], // Populated based on selected author
    characters: [], // Populated based on selected book
    genres: [],
    statuses: ['Open', 'Assigned', 'Completed', 'Cancelled'],
    priorities: ['High', 'Medium', 'Low']
  },

  // UI state
  ui: {
    advancedFiltersExpanded: false,
    isSearching: false,
    searchMode: 'manual' // 'manual', 'auto', 'hybrid' - defaulting to manual
  },

  // Favorites
  favorites: loadFavoritesFromStorage(),
  activeFavoriteId: null,

  // Loading states
  loading: {
    filterOptions: false,
    books: false,
    characters: false
  },

  // Error handling
  errors: {
    filterOptions: null,
    books: null,
    characters: null
  }
};

// Create the slice
const opportunityFiltersSlice = createSlice({
  name: 'opportunityFilters',
  initialState,
  reducers: {
    // Filter actions
    setBasicFilter: (state, action) => {
      const { field, value } = action.payload;
      state.filters.basic[field] = value;
      state.activeFavoriteId = null; // Clear active favorite when filters change
    },

    setAdvancedFilter: (state, action) => {
      const { field, value } = action.payload;
      state.filters.advanced[field] = value;
      state.activeFavoriteId = null;

      // Clear dependent filters
      if (field === 'authorIds') {
        // When authors change, clear dependent selections
        state.filters.advanced.bookId = null;
        state.filters.advanced.characterId = null;
        state.filterOptions.books = [];
        state.filterOptions.characters = [];
      } else if (field === 'bookId') {
        state.filters.advanced.characterId = null;
        state.filterOptions.characters = [];
      }
    },

    toggleGenre: (state, action) => {
      const genre = action.payload;
      const currentGenres = state.filters.basic.genres;
      const index = currentGenres.indexOf(genre);

      if (index === -1) {
        state.filters.basic.genres.push(genre);
      } else {
        state.filters.basic.genres.splice(index, 1);
      }
      state.activeFavoriteId = null;
    },

    clearGenres: (state) => {
      state.filters.basic.genres = [];
      state.activeFavoriteId = null;
    },

    toggleAuthor: (state, action) => {
      const authorId = action.payload;
      const currentAuthors = state.filters.advanced.authorIds;
      const index = currentAuthors.indexOf(authorId);

      if (index === -1) {
        state.filters.advanced.authorIds.push(authorId);
      } else {
        state.filters.advanced.authorIds.splice(index, 1);
      }

      // Clear dependent filters when authors change
      state.filters.advanced.bookId = null;
      state.filters.advanced.characterId = null;
      state.filterOptions.books = [];
      state.filterOptions.characters = [];
      state.activeFavoriteId = null;
    },

    clearAuthors: (state) => {
      state.filters.advanced.authorIds = [];
      state.filters.advanced.bookId = null;
      state.filters.advanced.characterId = null;
      state.filterOptions.books = [];
      state.filterOptions.characters = [];
      state.activeFavoriteId = null;
    },

    clearAllFilters: (state) => {
      state.filters = { ...initialState.filters };
      state.filterOptions.books = [];
      state.filterOptions.characters = [];
      state.activeFavoriteId = null;
    },

    // UI actions
    toggleAdvancedFilters: (state) => {
      state.ui.advancedFiltersExpanded = !state.ui.advancedFiltersExpanded;
    },

    setSearchMode: (state, action) => {
      state.ui.searchMode = action.payload;
    },

    setIsSearching: (state, action) => {
      state.ui.isSearching = action.payload;
    },

    // Favorites actions
    saveFavorite: (state, action) => {
      const { name, filters } = action.payload;
      const newFavorite = {
        id: `fav-${Date.now()}`,
        name,
        filters: JSON.parse(JSON.stringify(filters)),
        createdAt: new Date().toISOString(),
        isDefault: false
      };

      state.favorites.push(newFavorite);
      saveFavoritesToStorage(state.favorites);
    },

    loadFavorite: (state, action) => {
      const favorite = state.favorites.find(f => f.id === action.payload);
      if (favorite) {
        state.filters = JSON.parse(JSON.stringify(favorite.filters));
        state.activeFavoriteId = favorite.id;
      }
    },

    deleteFavorite: (state, action) => {
      state.favorites = state.favorites.filter(f => f.id !== action.payload);
      saveFavoritesToStorage(state.favorites);
      if (state.activeFavoriteId === action.payload) {
        state.activeFavoriteId = null;
      }
    },

    setDefaultFavorite: (state, action) => {
      state.favorites.forEach(f => {
        f.isDefault = f.id === action.payload;
      });
      saveFavoritesToStorage(state.favorites);
    },

    updateFavorite: (state, action) => {
      const { id, name } = action.payload;
      const favorite = state.favorites.find(f => f.id === id);
      if (favorite) {
        favorite.name = name;
        saveFavoritesToStorage(state.favorites);
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchFilterOptions
      .addCase(fetchFilterOptions.pending, (state) => {
        state.loading.filterOptions = true;
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        state.loading.filterOptions = false;
        state.filterOptions.authors = action.payload.authors;
        state.filterOptions.litGroups = action.payload.litGroups || [];
        state.filterOptions.genres = action.payload.genres;
        state.errors.filterOptions = null;
      })
      .addCase(fetchFilterOptions.rejected, (state, action) => {
        state.loading.filterOptions = false;
        state.errors.filterOptions = action.payload;
      })

      // Handle fetchBooksByAuthor
      .addCase(fetchBooksByAuthor.pending, (state) => {
        state.loading.books = true;
      })
      .addCase(fetchBooksByAuthor.fulfilled, (state, action) => {
        state.loading.books = false;
        state.filterOptions.books = action.payload;
        state.errors.books = null;
      })
      .addCase(fetchBooksByAuthor.rejected, (state, action) => {
        state.loading.books = false;
        state.errors.books = action.payload;
      })

      // Handle fetchCharactersByBook
      .addCase(fetchCharactersByBook.pending, (state) => {
        state.loading.characters = true;
      })
      .addCase(fetchCharactersByBook.fulfilled, (state, action) => {
        state.loading.characters = false;
        state.filterOptions.characters = action.payload;
        state.errors.characters = null;
      })
      .addCase(fetchCharactersByBook.rejected, (state, action) => {
        state.loading.characters = false;
        state.errors.characters = action.payload;
      })

      // ⭐ The LitGroup cascade writes the SAME filterOptions fields, so the dropdowns need no
      // knowledge of which cascade filled them — LitGroup or author/book, they render identically.
      .addCase(fetchBooksByLitGroup.pending, (state) => {
        state.loading.books = true;
      })
      .addCase(fetchBooksByLitGroup.fulfilled, (state, action) => {
        state.loading.books = false;
        state.filterOptions.books = action.payload;
        state.errors.books = null;
      })
      .addCase(fetchBooksByLitGroup.rejected, (state, action) => {
        state.loading.books = false;
        state.errors.books = action.payload;
      })
      .addCase(fetchCharactersByLitGroup.pending, (state) => {
        state.loading.characters = true;
      })
      .addCase(fetchCharactersByLitGroup.fulfilled, (state, action) => {
        state.loading.characters = false;
        state.filterOptions.characters = action.payload;
        state.errors.characters = null;
      })
      .addCase(fetchCharactersByLitGroup.rejected, (state, action) => {
        state.loading.characters = false;
        state.errors.characters = action.payload;
      });
  }
});

// Export actions
export const {
  setBasicFilter,
  setAdvancedFilter,
  toggleGenre,
  clearGenres,
  toggleAuthor,
  clearAuthors,
  clearAllFilters,
  toggleAdvancedFilters,
  setSearchMode,
  setIsSearching,
  saveFavorite,
  loadFavorite,
  deleteFavorite,
  setDefaultFavorite,
  updateFavorite
} = opportunityFiltersSlice.actions;

// Export selectors - Note: These still look for old name in state tree
// Will need to update store.js to use new name: opportunityFilters
export const selectFilters = (state) => state.user.opportunityFilters?.filters || initialState.filters;
export const selectFilterOptions = (state) => state.user.opportunityFilters?.filterOptions || initialState.filterOptions;
export const selectUI = (state) => state.user.opportunityFilters?.ui || initialState.ui;
// Stable empty-array fallback — a fresh `[]` per call makes useSelector warn
// ("returned a different result … same parameters") and rerender needlessly.
const EMPTY_ARRAY = Object.freeze([]);
export const selectFavorites = (state) => state.user.opportunityFilters?.favorites || EMPTY_ARRAY;
export const selectActiveFavoriteId = (state) => state.user.opportunityFilters?.activeFavoriteId;
export const selectLoading = (state) => state.user.opportunityFilters?.loading || initialState.loading;

// Export combined filter object for API calls
export const selectCombinedFilters = (state) => {
  const filters = state.user.opportunityFilters;
  if (!filters) return {};

  const combined = {};

  // Add basic filters
  if (filters.filters.basic.searchTerm) combined.searchTerm = filters.filters.basic.searchTerm;
  if (filters.filters.basic.status !== 'all') combined.status = filters.filters.basic.status;
  if (filters.filters.basic.genres?.length > 0) combined.genres = filters.filters.basic.genres;

  // Add advanced filters
  if (filters.filters.advanced.litGroupId) combined.litGroupId = filters.filters.advanced.litGroupId;
  if (filters.filters.advanced.authorIds?.length > 0) combined.authorIds = filters.filters.advanced.authorIds;
  if (filters.filters.advanced.bookId) combined.bookId = filters.filters.advanced.bookId;
  if (filters.filters.advanced.characterId) combined.characterId = filters.filters.advanced.characterId;
  if (filters.filters.advanced.priority) combined.priority = filters.filters.advanced.priority;

  return combined;
};

// Export reducer
export default opportunityFiltersSlice.reducer;

// ---------------------------------------------------------------------------
// Memoized version of selectCombinedFilters
// ---------------------------------------------------------------------------
// React-Redux v8+ in dev mode warns when a selector returns a different
// reference for the same input. The plain `selectCombinedFilters` above
// builds a fresh `combined` object on every call, so any unrelated store
// update causes the warning to fire on every consumer that uses it inside
// `useSelector`.
//
// `selectCombinedFiltersMemo` is the recommended replacement: it derives
// the same shape but is wrapped in `createSelector`, so the returned
// reference is stable as long as the underlying basic / advanced filter
// slices haven't changed. Prefer this in new code; existing callers can
// migrate at their leisure (the unmemoized version above is kept for
// backward compatibility only).
// ---------------------------------------------------------------------------

const selectFiltersBasic = (state) =>
  state.user.opportunityFilters?.filters?.basic ?? initialState.filters.basic;
const selectFiltersAdvanced = (state) =>
  state.user.opportunityFilters?.filters?.advanced ?? initialState.filters.advanced;

export const selectCombinedFiltersMemo = createSelector(
  [selectFiltersBasic, selectFiltersAdvanced],
  (basic, advanced) => {
    const combined = {};
    if (basic.searchTerm) combined.searchTerm = basic.searchTerm;
    if (basic.status !== 'all') combined.status = basic.status;
    if (basic.genres?.length > 0) combined.genres = basic.genres;
    if (advanced.litGroupId) combined.litGroupId = advanced.litGroupId;
    if (advanced.authorIds?.length > 0) combined.authorIds = advanced.authorIds;
    if (advanced.bookId) combined.bookId = advanced.bookId;
    if (advanced.characterId) combined.characterId = advanced.characterId;
    if (advanced.priority) combined.priority = advanced.priority;
    return combined;
  }
);
